import { useState, useMemo, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import ERPLayout from "@/components/feature/ERPLayout";
import {
  mockLeaveRequests,
  mockPublicHolidays,
  leaveBalances,
  LeaveRequest,
  LeaveStatus,
  LeaveType,
} from "@/mocks/leaves";
import { mockEmployeesFull } from "@/mocks/employees";

// ─── helpers ────────────────────────────────────────────────────────────────
function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}
function toYMD(d: Date) {
  return d.toISOString().split("T")[0];
}
function dateRange(from: string, to: string): string[] {
  const dates: string[] = [];
  const cur = new Date(from + "T00:00:00");
  const end = new Date(to + "T00:00:00");
  while (cur <= end) {
    dates.push(toYMD(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DAY_LABELS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const LEAVE_TYPE_COLORS: Record<LeaveType, { bg: string; text: string; dot: string }> = {
  "Casual Leave":     { bg: "#eff6ff", text: "#1d4ed8", dot: "#3b82f6" },
  "Sick Leave":       { bg: "#fef2f2", text: "#b91c1c", dot: "#ef4444" },
  "Earned Leave":     { bg: "#f0fdf4", text: "#15803d", dot: "#22c55e" },
  "Compensatory Off": { bg: "#fdf4ff", text: "#7e22ce", dot: "#a855f7" },
  "Unpaid Leave":     { bg: "#fff7ed", text: "#c2410c", dot: "#f97316" },
};
const STATUS_COLORS: Record<LeaveStatus, { bg: string; text: string }> = {
  Approved: { bg: "#ecfdf5", text: "#16a34a" },
  Pending:  { bg: "#fffbeb", text: "#d97706" },
  Rejected: { bg: "#fef2f2", text: "#dc2626" },
};
const LEAVE_TYPES: LeaveType[] = ["Casual Leave","Sick Leave","Earned Leave","Compensatory Off","Unpaid Leave"];

// ─── Audit trail entry ───────────────────────────────────────────────────────
interface AuditEntry {
  id: number;
  leaveId: number;
  action: "Submitted" | "Approved" | "Rejected" | "Cancelled";
  by: string;
  at: string;
  note?: string;
}

// ─── sub-components ──────────────────────────────────────────────────────────
function LeaveTypeBadge({ type }: { type: LeaveType }) {
  const c = LEAVE_TYPE_COLORS[type];
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: c.bg, color: c.text }}>
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: c.dot }} />
      {type}
    </span>
  );
}
function StatusBadge({ status }: { status: LeaveStatus }) {
  const c = STATUS_COLORS[status];
  return (
    <span className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: c.bg, color: c.text }}>
      {status}
    </span>
  );
}

// ─── Toast notification ───────────────────────────────────────────────────────
interface Toast { id: number; type: "success" | "error" | "info"; title: string; message: string; }

// ─── main page ───────────────────────────────────────────────────────────────
export default function LeaveCalendarPage() {
  const navigate = useNavigate();
  const today = new Date();
  const currentUser = (() => { try { return JSON.parse(localStorage.getItem("erp_user") || "{}"); } catch { return {}; } })();
  const isManager = ["admin","superadmin","super_admin","survey_manager"].includes(currentUser?.role || "");

  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"calendar" | "requests" | "holidays">("calendar");
  const [filterStatus, setFilterStatus] = useState<"All" | LeaveStatus>("All");
  const [filterDept, setFilterDept] = useState("All");
  const [leaves, setLeaves] = useState<LeaveRequest[]>(mockLeaveRequests);
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([
    { id: 1, leaveId: 1, action: "Submitted", by: "Mahesh Babu", at: "2026-04-13 09:15", note: "Leave applied" },
    { id: 2, leaveId: 1, action: "Approved", by: "Priya Sharma", at: "2026-04-13 11:30", note: "Approved with medical certificate" },
    { id: 3, leaveId: 6, action: "Submitted", by: "Suresh Kumar", at: "2026-04-10 08:00" },
    { id: 4, leaveId: 6, action: "Approved", by: "Priya Sharma", at: "2026-04-10 10:00" },
    { id: 5, leaveId: 8, action: "Submitted", by: "Venkat Reddy", at: "2026-04-06 07:45" },
    { id: 6, leaveId: 8, action: "Rejected", by: "Priya Sharma", at: "2026-04-06 09:00", note: "Insufficient notice period" },
  ]);
  const [toasts, setToasts] = useState<Toast[]>([]);

  // Modals
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState<LeaveRequest | null>(null);
  const [showRejectModal, setShowRejectModal] = useState<LeaveRequest | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showAuditModal, setShowAuditModal] = useState<LeaveRequest | null>(null);

  // Apply form
  const [applyForm, setApplyForm] = useState({ empId: "", type: "Casual Leave" as LeaveType, from: "", to: "", reason: "" });
  const [applyMsg, setApplyMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const [saving, setSaving] = useState(false);

  const departments = ["All", "Field Operations", "Office", "Management", "Lab"];

  // Toast helpers
  const addToast = useCallback((t: Omit<Toast, "id">) => {
    const id = Date.now();
    setToasts(prev => [...prev, { ...t, id }]);
    setTimeout(() => setToasts(prev => prev.filter(x => x.id !== id)), 4500);
  }, []);

  // Simulate email notification
  const simulateEmail = useCallback((to: string, subject: string, action: string) => {
    console.info(`[Email Notification] To: ${to} | Subject: ${subject} | Action: ${action}`);
  }, []);

  const leaveDateMap = useMemo(() => {
    const map: Record<string, LeaveRequest[]> = {};
    leaves.forEach((lr) => {
      dateRange(lr.from, lr.to).forEach((d) => {
        if (!map[d]) map[d] = [];
        map[d].push(lr);
      });
    });
    return map;
  }, [leaves]);

  const holidayMap = useMemo(() => {
    const m: Record<string, typeof mockPublicHolidays[0]> = {};
    mockPublicHolidays.forEach((h) => { m[h.date] = h; });
    return m;
  }, []);

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);
  const calendarCells: (number | null)[] = [...Array(firstDay).fill(null), ...Array.from({ length: daysInMonth }, (_, i) => i + 1)];
  while (calendarCells.length % 7 !== 0) calendarCells.push(null);

  const prevMonth = () => { if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); } else setViewMonth(m => m - 1); setSelectedDate(null); };
  const nextMonth = () => { if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); } else setViewMonth(m => m + 1); setSelectedDate(null); };

  const selectedDateLeaves = selectedDate ? (leaveDateMap[selectedDate] || []) : [];
  const selectedHoliday = selectedDate ? holidayMap[selectedDate] : null;

  const filteredLeaves = useMemo(() => {
    return leaves.filter((l) => {
      const matchStatus = filterStatus === "All" || l.status === filterStatus;
      const matchDept = filterDept === "All" || l.department === filterDept;
      return matchStatus && matchDept;
    });
  }, [leaves, filterStatus, filterDept]);

  const pendingCount = leaves.filter(l => l.status === "Pending").length;
  const approvedThisMonth = leaves.filter(l => { const d = new Date(l.from); return l.status === "Approved" && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear(); }).length;
  const onLeaveToday = (leaveDateMap[toYMD(today)] || []).filter(l => l.status === "Approved").length;
  const holidaysThisMonth = mockPublicHolidays.filter(h => { const d = new Date(h.date); return d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear(); }).length;

  // ── Approve ──────────────────────────────────────────────────────────────
  const handleApprove = useCallback((id: number) => {
    const leave = leaves.find(l => l.id === id);
    if (!leave) return;
    setSaving(true);
    setTimeout(() => {
      setLeaves(prev => prev.map(l => l.id === id ? { ...l, status: "Approved" as LeaveStatus, approvedBy: currentUser?.name || "Manager" } : l));
      setAuditLog(prev => [...prev, { id: Date.now(), leaveId: id, action: "Approved", by: currentUser?.name || "Manager", at: new Date().toLocaleString("en-IN"), note: "Approved via leave management" }]);
      simulateEmail(leave.empName + "@company.com", `Leave Approved — ${leave.type} (${leave.from} to ${leave.to})`, "APPROVED");
      addToast({ type: "success", title: "Leave Approved", message: `${leave.empName}'s ${leave.type} has been approved. Email notification sent.` });
      setShowDetailModal(null);
      setSaving(false);
    }, 600);
  }, [leaves, currentUser, simulateEmail, addToast]);

  // ── Reject ───────────────────────────────────────────────────────────────
  const handleRejectConfirm = useCallback(() => {
    if (!showRejectModal) return;
    if (!rejectReason.trim()) return;
    const leave = showRejectModal;
    setSaving(true);
    setTimeout(() => {
      setLeaves(prev => prev.map(l => l.id === leave.id ? { ...l, status: "Rejected" as LeaveStatus } : l));
      setAuditLog(prev => [...prev, { id: Date.now(), leaveId: leave.id, action: "Rejected", by: currentUser?.name || "Manager", at: new Date().toLocaleString("en-IN"), note: rejectReason }]);
      simulateEmail(leave.empName + "@company.com", `Leave Request Update — ${leave.type} (${leave.from} to ${leave.to})`, "REJECTED");
      addToast({ type: "error", title: "Leave Rejected", message: `${leave.empName}'s request rejected. Reason sent via email.` });
      setShowRejectModal(null);
      setShowDetailModal(null);
      setRejectReason("");
      setSaving(false);
    }, 600);
  }, [showRejectModal, rejectReason, currentUser, simulateEmail, addToast]);

  // ── Apply leave ──────────────────────────────────────────────────────────
  const handleApplyLeave = () => {
    if (!applyForm.empId || !applyForm.from || !applyForm.to || !applyForm.reason.trim()) {
      setApplyMsg({ type: "error", text: "Please fill all required fields." });
      return;
    }
    const emp = mockEmployeesFull.find(e => String(e.id) === applyForm.empId);
    if (!emp) { setApplyMsg({ type: "error", text: "Employee not found." }); return; }
    const fromD = new Date(applyForm.from + "T00:00:00");
    const toD = new Date(applyForm.to + "T00:00:00");
    if (toD < fromD) { setApplyMsg({ type: "error", text: "End date must be after start date." }); return; }
    const days = Math.round((toD.getTime() - fromD.getTime()) / 86400000) + 1;
    setSaving(true);
    setTimeout(() => {
      const newLeave: LeaveRequest = {
        id: Date.now(), empId: emp.id, empName: emp.name, empAvatar: emp.avatar,
        department: emp.department, type: applyForm.type, from: applyForm.from, to: applyForm.to,
        days, reason: applyForm.reason, status: "Pending", appliedOn: toYMD(today),
      };
      setLeaves(prev => [newLeave, ...prev]);
      setAuditLog(prev => [...prev, { id: Date.now(), leaveId: newLeave.id, action: "Submitted", by: emp.name, at: new Date().toLocaleString("en-IN"), note: applyForm.reason }]);
      simulateEmail("manager@company.com", `New Leave Request — ${emp.name} (${applyForm.type})`, "NEW_REQUEST");
      addToast({ type: "info", title: "Leave Request Submitted", message: `${emp.name}'s ${applyForm.type} request sent for approval. Manager notified.` });
      setShowApplyModal(false);
      setApplyForm({ empId: "", type: "Casual Leave", from: "", to: "", reason: "" });
      setApplyMsg(null);
      setSaving(false);
    }, 600);
  };

  const leaveAudit = (id: number) => auditLog.filter(a => a.leaveId === id);

  return (
    <ERPLayout title="Leave & Holiday Calendar">
      {/* ── Toast Stack ── */}
      <div className="fixed top-4 right-4 z-[60] space-y-2 pointer-events-none">
        {toasts.map(t => (
          <div key={t.id} className={`pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-xl text-sm font-medium min-w-[280px] max-w-sm animate-[slideIn_0.3s_ease] ${
            t.type === "success" ? "bg-amber-600 text-white" : t.type === "error" ? "bg-red-500 text-white" : "bg-gray-800 text-white"
          }`}>
            <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className={`${t.type === "success" ? "ri-checkbox-circle-line" : t.type === "error" ? "ri-close-circle-line" : "ri-information-line"} text-base`} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">{t.title}</p>
              <p className="text-xs opacity-90 mt-0.5 leading-relaxed">{t.message}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="p-6 space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <button onClick={() => navigate("/employees")} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 cursor-pointer mt-0.5">
              <i className="ri-arrow-left-line text-lg" />
            </button>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Leave &amp; Holiday Calendar</h2>
              <p className="text-sm text-gray-500 mt-0.5">Manage employee leaves, approvals &amp; public holidays</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/employees/availability"
              className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-team-line text-sm" /></span>
              Team Availability
            </Link>
            <button
              onClick={() => setShowApplyModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-sm" /></span>
              Apply Leave
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "On Leave Today", value: onLeaveToday, icon: "ri-user-unfollow-line", color: "#ef4444", bg: "#fef2f2" },
            { label: "Pending Approvals", value: pendingCount, icon: "ri-time-line", color: "#d97706", bg: "#fffbeb" },
            { label: "Approved This Month", value: approvedThisMonth, icon: "ri-checkbox-circle-line", color: "#16a34a", bg: "#ecfdf5" },
            { label: "Holidays This Month", value: holidaysThisMonth, icon: "ri-calendar-event-line", color: "#7c3aed", bg: "#f5f3ff" },
          ].map((k) => (
            <div key={k.label} className="bg-white rounded-xl border border-gray-100 p-4">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-3" style={{ background: k.bg }}>
                <i className={`${k.icon} text-base`} style={{ color: k.color }} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{k.value}</p>
              <p className="text-xs text-gray-500 mt-0.5">{k.label}</p>
            </div>
          ))}
        </div>

        {/* Pending approvals banner */}
        {isManager && pendingCount > 0 && (
          <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
            <span className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
              <i className="ri-alarm-warning-line text-amber-600 text-base" />
            </span>
            <div className="flex-1">
              <p className="text-sm font-semibold text-amber-800">{pendingCount} leave request{pendingCount > 1 ? "s" : ""} awaiting your approval</p>
              <p className="text-xs text-amber-600 mt-0.5">Review and approve or reject to notify employees via email</p>
            </div>
            <button onClick={() => { setActiveTab("requests"); setFilterStatus("Pending"); }} className="px-3 py-1.5 bg-amber-500 text-white text-xs font-semibold rounded-lg cursor-pointer whitespace-nowrap hover:bg-amber-600">
              Review Now
            </button>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
          {(["calendar", "requests", "holidays"] as const).map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activeTab === tab ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>
              {tab === "calendar" ? "Calendar View" : tab === "requests" ? `Leave Requests${pendingCount > 0 ? ` (${pendingCount})` : ""}` : "Public Holidays"}
            </button>
          ))}
        </div>

        {/* ── CALENDAR TAB ── */}
        {activeTab === "calendar" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-5">
                <button onClick={prevMonth} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 cursor-pointer"><i className="ri-arrow-left-s-line text-lg" /></button>
                <h3 className="text-base font-bold text-gray-900">{MONTH_NAMES[viewMonth]} {viewYear}</h3>
                <button onClick={nextMonth} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 cursor-pointer"><i className="ri-arrow-right-s-line text-lg" /></button>
              </div>
              <div className="grid grid-cols-7 mb-2">
                {DAY_LABELS.map((d) => (
                  <div key={d} className={`text-center text-xs font-semibold py-1 ${d === "Sun" ? "text-red-400" : "text-gray-400"}`}>{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {calendarCells.map((day, idx) => {
                  if (!day) return <div key={`e-${idx}`} />;
                  const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                  const isToday = dateStr === toYMD(today);
                  const isSelected = dateStr === selectedDate;
                  const dayLeaves = leaveDateMap[dateStr] || [];
                  const approvedLeaves = dayLeaves.filter(l => l.status === "Approved");
                  const pendingLeaves = dayLeaves.filter(l => l.status === "Pending");
                  const holiday = holidayMap[dateStr];
                  const isSunday = new Date(dateStr + "T00:00:00").getDay() === 0;
                  const isSaturday = new Date(dateStr + "T00:00:00").getDay() === 6;
                  return (
                    <button key={dateStr} onClick={() => setSelectedDate(isSelected ? null : dateStr)}
                      className={`relative min-h-[52px] p-1.5 rounded-xl text-left transition-all cursor-pointer border ${
                        isSelected ? "border-amber-400 bg-amber-50" : isToday ? "border-amber-200 bg-amber-50/50" : holiday ? "border-violet-100 bg-violet-50/40" : isSunday || isSaturday ? "border-transparent bg-gray-50/60" : "border-transparent hover:bg-gray-50"
                      }`}>
                      <span className={`text-xs font-semibold block mb-1 ${isToday ? "text-amber-600" : isSunday ? "text-red-400" : "text-gray-700"}`}>{day}</span>
                      {holiday && <span className="block w-full text-violet-600 font-medium truncate leading-tight mb-0.5" style={{ fontSize: "9px" }}>{holiday.name}</span>}
                      <div className="flex flex-wrap gap-0.5">
                        {approvedLeaves.slice(0, 2).map((l) => (
                          <span key={l.id} className="w-4 h-4 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0" style={{ background: "#10b981", fontSize: "8px" }} title={`${l.empName} — ${l.type}`}>{l.empAvatar[0]}</span>
                        ))}
                        {pendingLeaves.slice(0, 1).map((l) => (
                          <span key={l.id} className="w-4 h-4 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0" style={{ background: "#f59e0b", fontSize: "8px" }} title={`${l.empName} — Pending`}>{l.empAvatar[0]}</span>
                        ))}
                        {dayLeaves.length > 3 && <span className="text-gray-400 font-bold" style={{ fontSize: "9px" }}>+{dayLeaves.length - 3}</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-gray-50">
                {[["bg-amber-500","Approved Leave"],["bg-amber-400","Pending Leave"],["bg-violet-400","Public Holiday"]].map(([cls, lbl]) => (
                  <span key={lbl} className="flex items-center gap-1.5 text-xs text-gray-500"><span className={`w-3 h-3 rounded-full ${cls} inline-block`} />{lbl}</span>
                ))}
              </div>
            </div>

            {/* Right panel */}
            <div className="space-y-4">
              {selectedDate ? (
                <div className="bg-white rounded-xl border border-gray-100 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-800">{new Date(selectedDate + "T00:00:00").toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" })}</h4>
                    <button onClick={() => setSelectedDate(null)} className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"><i className="ri-close-line text-sm" /></button>
                  </div>
                  {selectedHoliday && (
                    <div className="flex items-center gap-2 p-2.5 bg-violet-50 rounded-lg border border-violet-100 mb-3">
                      <i className="ri-calendar-event-line text-violet-500 text-sm" />
                      <div><p className="text-xs font-semibold text-violet-700">{selectedHoliday.name}</p><p className="text-xs text-violet-500">{selectedHoliday.type} Holiday</p></div>
                    </div>
                  )}
                  {selectedDateLeaves.length === 0 && !selectedHoliday && <p className="text-sm text-gray-400 text-center py-4">No leaves on this day</p>}
                  <div className="space-y-2">
                    {selectedDateLeaves.map((l) => (
                      <div key={l.id} className="flex items-center gap-2.5 p-2.5 rounded-lg border border-gray-100 hover:border-amber-100 cursor-pointer transition-all" onClick={() => setShowDetailModal(l)}>
                        <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{l.empAvatar}</div>
                        <div className="flex-1 min-w-0"><p className="text-xs font-semibold text-gray-800 truncate">{l.empName}</p><LeaveTypeBadge type={l.type} /></div>
                        <StatusBadge status={l.status} />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl border border-gray-100 p-4"><p className="text-xs text-gray-400 text-center py-4">Click a date to see details</p></div>
              )}

              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Upcoming Leaves</h4>
                <div className="space-y-2">
                  {leaves.filter(l => l.status !== "Rejected" && new Date(l.from + "T00:00:00") >= today).sort((a, b) => new Date(a.from).getTime() - new Date(b.from).getTime()).slice(0, 5).map((l) => (
                    <div key={l.id} className="flex items-center gap-2.5 cursor-pointer hover:bg-gray-50 rounded-lg p-1.5 -mx-1.5 transition-all" onClick={() => setShowDetailModal(l)}>
                      <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{l.empAvatar}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-gray-800 truncate">{l.empName}</p>
                        <p className="text-xs text-gray-400">{new Date(l.from + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" })}{l.from !== l.to && ` – ${new Date(l.to + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`} · {l.days}d</p>
                      </div>
                      <StatusBadge status={l.status} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-100 p-4">
                <h4 className="text-sm font-semibold text-gray-800 mb-3">Upcoming Holidays</h4>
                <div className="space-y-2">
                  {mockPublicHolidays.filter(h => new Date(h.date + "T00:00:00") >= today).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).slice(0, 4).map((h) => (
                    <div key={h.id} className="flex items-center gap-2.5 p-2 rounded-lg bg-violet-50/60 border border-violet-100">
                      <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0"><i className="ri-calendar-event-line text-violet-500 text-sm" /></div>
                      <div className="flex-1 min-w-0"><p className="text-xs font-semibold text-gray-800 truncate">{h.name}</p><p className="text-xs text-gray-400">{new Date(h.date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" })}</p></div>
                      <span className="text-xs text-violet-500 font-medium whitespace-nowrap">{h.type}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── REQUESTS TAB ── */}
        {activeTab === "requests" && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-col sm:flex-row gap-3 flex-wrap">
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-wrap">
                {(["All", "Pending", "Approved", "Rejected"] as const).map((s) => (
                  <button key={s} onClick={() => setFilterStatus(s)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filterStatus === s ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>
                    {s}{s === "Pending" && pendingCount > 0 && <span className="ml-1 bg-amber-400 text-white text-xs rounded-full px-1.5 py-0.5 font-bold">{pendingCount}</span>}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 flex-wrap">
                {departments.map((d) => (
                  <button key={d} onClick={() => setFilterDept(d)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${filterDept === d ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-800">Leave Requests</h3>
                <span className="text-xs text-gray-400">{filteredLeaves.length} records</span>
              </div>
              {filteredLeaves.length === 0 ? (
                <div className="text-center py-12"><i className="ri-calendar-check-line text-4xl text-gray-200 block mb-2" /><p className="text-sm text-gray-400">No leave requests found</p></div>
              ) : (
                <div className="divide-y divide-gray-50">
                  {filteredLeaves.map((l) => (
                    <div key={l.id} className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50/60 cursor-pointer transition-all group" onClick={() => setShowDetailModal(l)}>
                      <div className="w-9 h-9 rounded-full bg-amber-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">{l.empAvatar}</div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <p className="text-sm font-semibold text-gray-800">{l.empName}</p>
                          <span className="text-xs text-gray-400">{l.department}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <LeaveTypeBadge type={l.type} />
                          <span className="text-xs text-gray-400">{new Date(l.from + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" })}{l.from !== l.to && ` – ${new Date(l.to + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`}</span>
                          <span className="text-xs font-medium text-gray-600">{l.days} day{l.days > 1 ? "s" : ""}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <StatusBadge status={l.status} />
                        <button onClick={(e) => { e.stopPropagation(); setShowAuditModal(l); }} className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-300 hover:text-gray-600 hover:bg-gray-100 cursor-pointer transition-all opacity-0 group-hover:opacity-100" title="View audit trail">
                          <i className="ri-history-line text-sm" />
                        </button>
                        {l.status === "Pending" && isManager && (
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                            <button onClick={(e) => { e.stopPropagation(); handleApprove(l.id); }} className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-50 text-amber-600 hover:bg-amber-100 cursor-pointer" title="Approve"><i className="ri-check-line text-sm" /></button>
                            <button onClick={(e) => { e.stopPropagation(); setShowRejectModal(l); setShowDetailModal(null); }} className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 cursor-pointer" title="Reject"><i className="ri-close-line text-sm" /></button>
                          </div>
                        )}
                        <i className="ri-arrow-right-s-line text-gray-300 text-lg" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Leave balance */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-4">Leave Balance Summary</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left text-xs font-semibold text-gray-500 pb-2 pr-4">Employee</th>
                      {["Casual","Sick","Earned","Comp Off"].map(h => <th key={h} className="text-center text-xs font-semibold text-gray-500 pb-2 px-3">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {mockEmployeesFull.map((emp) => {
                      const bal = leaveBalances[emp.id] || { casual: 0, sick: 0, earned: 0, compOff: 0 };
                      return (
                        <tr key={emp.id} className="hover:bg-gray-50/50">
                          <td className="py-2.5 pr-4">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{emp.avatar}</div>
                              <div><p className="text-xs font-semibold text-gray-800">{emp.name}</p><p className="text-xs text-gray-400">{emp.role}</p></div>
                            </div>
                          </td>
                          {[bal.casual, bal.sick, bal.earned, bal.compOff].map((v, i) => (
                            <td key={i} className="text-center py-2.5 px-3">
                              <span className={`text-xs font-bold ${v <= 2 ? "text-red-500" : v <= 5 ? "text-amber-500" : "text-amber-600"}`}>{v}</span>
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ── HOLIDAYS TAB ── */}
        {activeTab === "holidays" && (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800">Public Holidays {viewYear}</h3>
              <span className="text-xs text-gray-400">{mockPublicHolidays.length} holidays</span>
            </div>
            <div className="divide-y divide-gray-50">
              {[...mockPublicHolidays].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).map((h) => {
                const d = new Date(h.date + "T00:00:00");
                const isPast = d < today;
                return (
                  <div key={h.id} className={`flex items-center gap-4 px-5 py-3.5 ${isPast ? "opacity-50" : ""}`}>
                    <div className="w-12 h-12 rounded-xl bg-violet-50 border border-violet-100 flex flex-col items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-violet-600 leading-none">{d.toLocaleDateString("en-IN", { month: "short" }).toUpperCase()}</span>
                      <span className="text-lg font-black text-violet-700 leading-tight">{d.getDate()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800">{h.name}</p>
                      <p className="text-xs text-gray-400">{d.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${h.type === "National" ? "bg-amber-50 text-amber-700" : h.type === "Regional" ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-600"}`}>{h.type}</span>
                      {!isPast && <span className="text-xs text-gray-400">{Math.ceil((d.getTime() - today.getTime()) / 86400000)}d away</span>}
                      {isPast && <span className="text-xs text-gray-300">Past</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── APPLY LEAVE MODAL ── */}
      {showApplyModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">Apply for Leave</h3>
              <button onClick={() => { setShowApplyModal(false); setApplyForm({ empId: "", type: "Casual Leave", from: "", to: "", reason: "" }); setApplyMsg(null); }} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-lg" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Employee <span className="text-red-400">*</span></label>
                <select value={applyForm.empId} onChange={(e) => setApplyForm({ ...applyForm, empId: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer">
                  <option value="">Select employee...</option>
                  {mockEmployeesFull.map((e) => <option key={e.id} value={String(e.id)}>{e.name} — {e.role}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Leave Type <span className="text-red-400">*</span></label>
                <div className="flex flex-wrap gap-2">
                  {LEAVE_TYPES.map((t) => {
                    const c = LEAVE_TYPE_COLORS[t];
                    return (
                      <button key={t} onClick={() => setApplyForm({ ...applyForm, type: t })} className="px-2.5 py-1.5 border rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap transition-all"
                        style={applyForm.type === t ? { borderColor: c.dot, background: c.bg, color: c.text } : { borderColor: "#e5e7eb", color: "#6b7280" }}>
                        {t}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">From Date <span className="text-red-400">*</span></label>
                  <input type="date" value={applyForm.from} onChange={(e) => setApplyForm({ ...applyForm, from: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">To Date <span className="text-red-400">*</span></label>
                  <input type="date" value={applyForm.to} min={applyForm.from} onChange={(e) => setApplyForm({ ...applyForm, to: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer" />
                </div>
              </div>
              {applyForm.from && applyForm.to && applyForm.to >= applyForm.from && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-100">
                  <i className="ri-calendar-check-line text-amber-500 text-sm" />
                  <span className="text-xs text-amber-700 font-medium">{Math.round((new Date(applyForm.to + "T00:00:00").getTime() - new Date(applyForm.from + "T00:00:00").getTime()) / 86400000) + 1} day(s) selected</span>
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Reason <span className="text-red-400">*</span></label>
                <textarea rows={3} placeholder="Briefly describe the reason for leave..." value={applyForm.reason} onChange={(e) => setApplyForm({ ...applyForm, reason: e.target.value })} maxLength={500} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 resize-none" />
                <p className="text-xs text-gray-400 text-right mt-0.5">{applyForm.reason.length}/500</p>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
                <i className="ri-mail-send-line text-gray-400 text-sm" />
                <span className="text-xs text-gray-500">Manager will be notified via email upon submission</span>
              </div>
              {applyMsg && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${applyMsg.type === "success" ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                  <i className={`${applyMsg.type === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} text-base`} />{applyMsg.text}
                </div>
              )}
              <div className="flex items-center gap-3 pt-1">
                <button onClick={() => { setShowApplyModal(false); setApplyForm({ empId: "", type: "Casual Leave", from: "", to: "", reason: "" }); setApplyMsg(null); }} className="flex-1 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Cancel</button>
                <button onClick={handleApplyLeave} disabled={saving} className="flex-1 py-2 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 cursor-pointer whitespace-nowrap disabled:opacity-50">
                  {saving ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── LEAVE DETAIL MODAL ── */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">Leave Details</h3>
              <button onClick={() => setShowDetailModal(null)} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-lg" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center text-white text-base font-bold flex-shrink-0">{showDetailModal.empAvatar}</div>
                <div><p className="text-sm font-bold text-gray-900">{showDetailModal.empName}</p><p className="text-xs text-gray-500">{showDetailModal.department}</p></div>
                <div className="ml-auto"><StatusBadge status={showDetailModal.status} /></div>
              </div>
              <div className="space-y-2.5 bg-gray-50 rounded-xl p-4">
                {[
                  { label: "Leave Type", value: <LeaveTypeBadge type={showDetailModal.type} /> },
                  { label: "From", value: new Date(showDetailModal.from + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "long", year: "numeric" }) },
                  { label: "To", value: new Date(showDetailModal.to + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "long", year: "numeric" }) },
                  { label: "Duration", value: `${showDetailModal.days} day${showDetailModal.days > 1 ? "s" : ""}` },
                  { label: "Applied On", value: new Date(showDetailModal.appliedOn + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) },
                  ...(showDetailModal.approvedBy ? [{ label: "Approved By", value: showDetailModal.approvedBy }] : []),
                ].map((row) => (
                  <div key={row.label} className="flex items-start justify-between gap-3">
                    <span className="text-xs text-gray-500 flex-shrink-0">{row.label}</span>
                    <span className="text-xs font-medium text-gray-800 text-right">{row.value}</span>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-xs font-medium text-gray-600 mb-1">Reason</p>
                <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 leading-relaxed">{showDetailModal.reason}</p>
              </div>
              {/* Audit trail preview */}
              {leaveAudit(showDetailModal.id).length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-2">Activity Trail</p>
                  <div className="space-y-1.5">
                    {leaveAudit(showDetailModal.id).map((a) => (
                      <div key={a.id} className="flex items-start gap-2">
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${a.action === "Approved" ? "bg-amber-100" : a.action === "Rejected" ? "bg-red-100" : "bg-gray-100"}`}>
                          <i className={`text-xs ${a.action === "Approved" ? "ri-check-line text-amber-600" : a.action === "Rejected" ? "ri-close-line text-red-500" : "ri-add-line text-gray-500"}`} />
                        </span>
                        <div>
                          <p className="text-xs font-medium text-gray-700">{a.action} by {a.by}</p>
                          <p className="text-xs text-gray-400">{a.at}{a.note ? ` — ${a.note}` : ""}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {showDetailModal.status === "Pending" && isManager && (
                <div className="flex items-center gap-3 pt-1">
                  <button onClick={() => { setShowRejectModal(showDetailModal); setShowDetailModal(null); }} className="flex-1 py-2 border border-red-200 text-red-500 rounded-xl text-sm font-semibold hover:bg-red-50 cursor-pointer whitespace-nowrap"><i className="ri-close-line mr-1" />Reject</button>
                  <button onClick={() => handleApprove(showDetailModal.id)} disabled={saving} className="flex-1 py-2 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 cursor-pointer whitespace-nowrap disabled:opacity-50">
                    {saving ? "..." : <><i className="ri-check-line mr-1" />Approve</>}
                  </button>
                </div>
              )}
              {showDetailModal.status !== "Pending" && (
                <button onClick={() => setShowDetailModal(null)} className="w-full py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Close</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── REJECT WITH REASON MODAL ── */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">Reject Leave Request</h3>
              <button onClick={() => { setShowRejectModal(null); setRejectReason(""); }} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-lg" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center gap-3 p-3 bg-red-50 rounded-xl border border-red-100">
                <div className="w-9 h-9 rounded-full bg-amber-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">{showRejectModal.empAvatar}</div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{showRejectModal.empName}</p>
                  <p className="text-xs text-gray-500">{showRejectModal.type} · {showRejectModal.days} day{showRejectModal.days > 1 ? "s" : ""} · {new Date(showRejectModal.from + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Rejection Reason <span className="text-red-400">*</span></label>
                <textarea rows={3} placeholder="Provide a reason for rejection (will be sent to employee via email)..." value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} maxLength={500} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-400 resize-none" />
                <p className="text-xs text-gray-400 text-right mt-0.5">{rejectReason.length}/500</p>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100">
                <i className="ri-mail-send-line text-gray-400 text-sm" />
                <span className="text-xs text-gray-500">Employee will be notified via email with this reason</span>
              </div>
              <div className="flex items-center gap-3">
                <button onClick={() => { setShowRejectModal(null); setRejectReason(""); }} className="flex-1 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Cancel</button>
                <button onClick={handleRejectConfirm} disabled={!rejectReason.trim() || saving} className="flex-1 py-2 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600 cursor-pointer whitespace-nowrap disabled:opacity-50">
                  {saving ? "Rejecting..." : "Confirm Reject"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── AUDIT TRAIL MODAL ── */}
      {showAuditModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">Audit Trail</h3>
              <button onClick={() => setShowAuditModal(null)} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-lg" /></button>
            </div>
            <div className="p-6">
              <div className="flex items-center gap-2 mb-4 p-3 bg-gray-50 rounded-xl">
                <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{showAuditModal.empAvatar}</div>
                <div><p className="text-xs font-semibold text-gray-800">{showAuditModal.empName}</p><p className="text-xs text-gray-400">{showAuditModal.type} · {showAuditModal.from} to {showAuditModal.to}</p></div>
              </div>
              {leaveAudit(showAuditModal.id).length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No audit entries found</p>
              ) : (
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-100" />
                  <div className="space-y-4">
                    {leaveAudit(showAuditModal.id).map((a, i) => (
                      <div key={a.id} className="flex items-start gap-3 relative">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 z-10 ${
                          a.action === "Approved" ? "bg-amber-100 border-2 border-amber-300" :
                          a.action === "Rejected" ? "bg-red-100 border-2 border-red-300" :
                          a.action === "Submitted" ? "bg-gray-100 border-2 border-gray-300" :
                          "bg-amber-100 border-2 border-amber-300"
                        }`}>
                          <i className={`text-sm ${
                            a.action === "Approved" ? "ri-check-line text-amber-600" :
                            a.action === "Rejected" ? "ri-close-line text-red-500" :
                            a.action === "Submitted" ? "ri-file-add-line text-gray-500" :
                            "ri-delete-bin-line text-amber-600"
                          }`} />
                        </div>
                        <div className="flex-1 pt-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-xs font-bold ${
                              a.action === "Approved" ? "text-amber-600" :
                              a.action === "Rejected" ? "text-red-500" : "text-gray-700"
                            }`}>{a.action}</span>
                            <span className="text-xs text-gray-500">by {a.by}</span>
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5">{a.at}</p>
                          {a.note && <p className="text-xs text-gray-600 mt-1 bg-gray-50 rounded-lg px-2 py-1">{a.note}</p>}
                        </div>
                        {i < leaveAudit(showAuditModal.id).length - 1 && <div className="absolute left-4 top-8 bottom-0 w-px bg-gray-100" />}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <button onClick={() => setShowAuditModal(null)} className="w-full mt-5 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap">Close</button>
            </div>
          </div>
        </div>
      )}
    </ERPLayout>
  );
}
